import React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';

import {
    Spacer,
    Text,
} from '@zillow/constellation';
import {
    ContentBox,
} from '@zillow/zrs-comet';

import analyticsUtils from 'app/client/utils/analyticsUtils';
import AnalyticsConstants from 'app/shared/constants/AnalyticsConstants';
import PhotoUpload from 'app/shared/components/postingPath/PhotoUpload';
import ListingTour from 'app/shared/components/postingPath/ListingTour';
import { PropertiesSelectors } from 'app/shared/selectors';

import {
    StyledLink,
} from './StyledComponents';

const propTypes = {
    tour: PropTypes.shape({
        name: PropTypes.string.isRequired,
        createdDate: PropTypes.string.isRequired,
        creatorName: PropTypes.string.isRequired,
        imageSrc: PropTypes.string,
        state: PropTypes.oneOf(['active', 'inactive', 'empty']),
    }),
    tourType: PropTypes.oneOf(['amenity', 'singleFamily']),
};

const defaultProps = {
    tour: {
        name: '',
        createdDate: '',
        creatorName: '',
        imageSrc: '',
        state: 'empty',
    },
    tourType: 'amenity',
};

// multifamily vars
const sectionTitleMF = 'Building Media';

// single family vars
const sectionTitleSF = 'Property media';
const subSectionTour = '3D tour';
const subSectionPhotos = 'Photos';
const noTourContent = 'Let renters tour your home virtually by adding a free 3D tour.';
const learnMore3dTourUrl = 'https://www.zillow.com/marketing/3d-home/';

const Media = ({
    tour,
    tourType,
    errorObj,
    formData,
    propertyId,
    isListingPhotoProcessing,
    updateListingPhotoProcessingStatus,
    updateFormData,
    updateErrorObj,
    updateStatusMessage,
    statusMessage,
    isInputFormDisabled,
}) => {
    const dispatch = useDispatch();
    const { hasVrModels: show3dTours } = useSelector(state => PropertiesSelectors.makeCurrentProperty(state));

    const handleLearnMoreTourClick = () => {
        dispatch(analyticsUtils.track.GAEventV2(AnalyticsConstants.GA_EVENTS.LEARN_MORE_TOUR_CLICK, {
            label: AnalyticsConstants.TOUR_LOCATION.CARD,
        }));
    };

    const renderNo3dTourSF = () => (
        <React.Fragment>
            <Text
                marginBottom="xs"
                mediaQuery={{ lg: { marginBottom: 'sm' } }}
                fontType="h4"
                as="div"
            >
                {sectionTitleSF}
            </Text>

            <Spacer marginBottom="sm" className="SubSection-Tour">
                <Text
                    marginBottom="xs"
                    mediaQuery={{ lg: { marginBottom: 'sm' } }}
                    fontType="bodyheading"
                    as="div"
                >
                    {subSectionTour}
                </Text>

                <ContentBox highlight="blue">
                    <Text>
                        {noTourContent}
                    </Text>

                    <StyledLink
                        onClick={handleLearnMoreTourClick}
                        to={learnMore3dTourUrl}
                        target="_blank"
                    >
                        &nbsp;Learn More
                    </StyledLink>
                </ContentBox>
            </Spacer>

            <div className="SubSection-Photos">
                <Text
                    marginBottom="xs"
                    mediaQuery={{ lg: { marginBottom: 'sm' } }}
                    fontType="bodyheading"
                    as="div"
                >
                    {subSectionPhotos}
                </Text>
            </div>
        </React.Fragment>
    );

    const renderNo3dToursMF = () => (
        <Text className="PostingPath-module-title" type="h2">
                Building photos
        </Text>
    );

    const renderNo3dTours = () => (tourType === 'amenity' ? renderNo3dToursMF() : renderNo3dTourSF());

    return (
        <div className="Media">
            {show3dTours && (
                <React.Fragment>
                    <Text
                        marginBottom="xs"
                        mediaQuery={{
                            lg: {
                                marginBottom: 'sm',
                            },
                        }}
                        fontType="h2"
                        as="div"
                    >
                        {tourType === 'amenity' ? sectionTitleMF : sectionTitleSF}
                    </Text>
                    <ListingTour
                        formData={formData}
                        tour={tour}
                        tourType={tourType}
                        isDisabled={isInputFormDisabled}
                        updateStatusMessage={updateStatusMessage}
                        updateFormData={updateFormData}
                    />
                </React.Fragment>
            )}
            {show3dTours ?
                (
                    <Text className="PostingPath-module-subtitle" type="h4" emphasize={true}>
                        {tourType === 'amenity' ? 'Building Photos' : 'Photos'}
                    </Text>
                ) : renderNo3dTours()
            }
            <PhotoUpload
                showHeading={false}
                errorObj={errorObj}
                formData={formData}
                hasError={Boolean(errorObj.photos)}
                propertyId={propertyId}
                isListingPhotoProcessing={isListingPhotoProcessing}
                updateListingPhotoProcessingStatus={updateListingPhotoProcessingStatus}
                updateFormData={updateFormData}
                updateErrorObj={updateErrorObj}
                updateStatusMessage={updateStatusMessage}
                statusMessage={statusMessage}
                isInputFormDisabled={isInputFormDisabled}
            />
        </div>
    );
};

Media.propTypes = propTypes;
Media.defaultProps = defaultProps;

export { Media };
export default Media;
